import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Typography,
  FormControl, InputLabel, Select, MenuItem,
  DialogContentText, Alert, IconButton,
  ThemeProvider, createTheme
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormError } from '../../../components/Datatable/FormErrorProvider';
import { useParams } from 'react-router-dom';

export default function DomainForm({ open, id, categories, onClose, onSubmit }) {
  const error = useFormError();
  const fileInputRef = useRef(null);
  const { categoryId} = useParams();
  const [formData, setFormData] = useState({
    blacklist: '',
    categoryId: categoryId,
    file: null
  });
  
  const [fileError, setFileError] = useState('');

  // Reset form data when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        blacklist: '',
        categoryId: categoryId,
        file: null
      });
      setFileError('');
      error.setErrors([]);
    }
  }, [open, categories]);

  // Handle text field changes
  const onChangeDomains = (event) => {
    const inputValue = event.target.value;
    const regex = /^[a-zA-Z0-9,\-.\n ]+$/;
    if (regex.test(inputValue) || inputValue === '') {
      setFormData({ ...formData, blacklist: inputValue });
    }
  };

  // Handle category selection
  const onChangeCategory = (event) => {
    setFormData({ ...formData, categoryId: event.target.value });
  };

  // Handle file upload
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setFileError('');
    
    if (!file) return;
    
    // Check file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setFileError('O arquivo não pode ter mais de 5MB');
      return;
    }
    
    // Check file type
    const validTypes = ['text/plain', 'text/csv', 'application/csv', 'application/vnd.ms-excel'];
    if (!validTypes.includes(file.type)) {
      setFileError('Apenas arquivos de texto (.txt) ou CSV (.csv) são permitidos');
      return;
    }
    
    setFormData({ ...formData, file });
  };

  // Remove selected file
  const handleRemoveFile = () => {
    setFormData({ ...formData, file: null });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Trigger file input click
  const handleClickUpload = () => {
    fileInputRef.current.click();
  };

  // Form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setFileError('');

    // Validate
    if (!formData.blacklist.trim() && !formData.file) {
      error.setErrors([{ field: "blacklist", message: "Lista de domínios ou arquivo não pode estar vazio" }]);
      return;
    }

    // Prepare form data for submission
    const submitData = new FormData();
    submitData.append('category', formData.categoryId);
    
    if (formData.blacklist.trim()) {
      submitData.append('blacklist', formData.blacklist.trim());
    }
    
    if (formData.file) {
      submitData.append('file', formData.file);
    }

    onSubmit(submitData);
  };

  return (
    <ThemeProvider theme={createTheme({
      typography: {
        fontFamily: [
          'sans-serif',
          'Roboto',
          'Montserrat',
        ]
      },
      palette: {
        mode: 'light',
        primary: {
          main: "#e01b29",
        },
      }
    })}>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {categoryId ? "Adicionar Domínios a:" + ` ${categories?.find(cat => cat.id == categoryId)?.name}` : "Adicionar Domínios"}
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Digite os domínios que deseja adicionar à lista de bloqueio ou faça upload de um arquivo.
              Você pode inserir um domínio por linha ou separados por vírgula.
            </DialogContentText>
            {/* { !categoryId && 
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="category-select-label">Categoria</InputLabel>
              <Select
                labelId="category-select-label"
                value={formData.categoryId}
                label="Categoria"
                onChange={onChangeCategory}
              >
                {categories?.map((category) => (
                  <MenuItem
                    key={category.id}
                    value={category.id}
                    disabled={!category.active}
                  >
                    {category.name} {!category.active && "(Inativa)"}
                  </MenuItem>
                ))} */}
              {/* </Select>
            </FormControl>} */}

            <TextField
              multiline
              rows={10}
              fullWidth
              error={error.verify("blacklist")}
              helperText={error.msgError("blacklist") || "Digite um domínio por linha ou separados por vírgula"}
              label='Domínios'
              name="blacklist"
              value={formData.blacklist}
              onChange={onChangeDomains}
              placeholder="exemplo.com.br
            outro-exemplo.com
            terceiro.exemplo.net"
              sx={{ mb: 3 }}
            />

            <Box sx={{ mt: 2, mb: 2, border: '1px dashed #ccc', borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Ou faça upload de um arquivo (.txt ou .csv)
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleClickUpload}
                >
                  Selecionar arquivo
                </Button>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.csv"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                
                {formData.file && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography>
                      {formData.file.name} ({(formData.file.size / 1024).toFixed(2)} KB)
                    </Typography>
                    <IconButton size="small" onClick={handleRemoveFile}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
              
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Tamanho máximo: 5MB. Formatos permitidos: TXT, CSV.
              </Typography>
              
              {fileError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {fileError}
                </Alert>
              )}
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={onClose}>Cancelar</Button>
            <Button type="submit" variant="contained" color="primary">
              {id ? 'Atualizar' : 'Adicionar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </ThemeProvider>
  );
}